// src/pages/profit.ts —— 06 C-32 利润台账（0047；只读，权限 profit.view，明细 CSV 需 profit.export）。
// 数据：GET /api/admin/profit/summary（tab = type / agent / day / product，分组按 page / size 切片，响应头带 totals 等汇总）与
// GET /api/admin/profit/entries（明细）。复用 0107 表格（日期区间、facet、统计卡、pageNote、CSV）与 0103 页签，不新增模板。
// 汇总四页签的统计卡取各自响应头（每页都带 totals，无需 page=1 缓存）；明细页签不出统计卡（筛选维度不同，避免与汇总口径混淆）。
// 代理 / 产品 facet 选项为 id，展示名取响应 labels（模块级缓存：facetLabel 签名无宿主，缓存只存展示名、随每次响应覆盖）
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { fmtAmount } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openDrawer } from '../domains/table';
import { str, type AdminCtx } from '../domains/types';
import { I18N_PROFIT, REWRITES_PROFIT } from '../i18n/profit';
import { can } from '../perm';
import type { ProfitEntryRow, ProfitGroupRow, ProfitLabels, ProfitSums, ProfitSummaryHead } from './profit.types';
import { defineView, type ActionDef, type ColDef, type DrawerAction, type DrawerSpec, type PageMap, type StatVals } from './types';
import { cell, type CellVals } from './ui';
import { open360 } from './users';
import type { U360Tab } from './users.types';

const GRAY = 'color:#8a958f';
const RED = 'color:#d1434b';
const DP = 3; // USDT 展示 3 位（充值费 / 汇率差精度 0.001）

// ---- 格式化（D8-12 截断展示；后端非法值原样显示，不抛错） ----

function amt(v: string): string {
  try {
    return fmtAmount(v, DP);
  } catch {
    return v;
  }
}

// 有符号金额：负号用 U+2212（重组损失、调账为负）
const signed = (v: string): string => (v.trim().startsWith('-') ? `−${amt(v.trim().slice(1))}` : amt(v));
const usdt = (v: string): string => `${signed(v)} USDT`;
const negative = (v: string): boolean => v.trim().startsWith('-') && /[1-9]/.test(v);
const time = (ctx: AdminCtx, iso: string | null | undefined): string => fmtDateTime(iso, { tz: ctx.s.sh.timezone || undefined });

// ---- 映射 ----

const TYPE_KEY: Readonly<Record<string, string>> = {
  card_open: 'pfTypeCardOpen', card_withdraw: 'pfTypeCardWithdraw', card_close: 'pfTypeCardClose', card_tx_fee: 'pfTypeCardTxFee',
  deposit_fee: 'pfTypeDepositFee', deposit_fx_platform: 'pfTypeDepositFxPlatform', deposit_fx_agent: 'pfTypeDepositFxAgent',
  withdraw_fee: 'pfTypeWithdrawFee', withdraw_activation: 'pfTypeWithdrawActivation', agent_wd_fee: 'pfTypeAgentWdFee',
  deposit_reorg_loss: 'pfTypeReorgLoss', manual_adjust: 'pfTypeManualAdjust',
};
const SOURCE_KEY: Readonly<Record<string, string>> = {
  card_action: 'pfSrcCardAction', card_transaction: 'pfSrcCardTx', deposit: 'pfSrcDeposit', withdrawal: 'pfSrcWithdrawal',
  agent_withdrawal: 'pfSrcAgentWd', crypto_reorg_hold: 'pfSrcReorg', wallet_ledger: 'pfSrcLedger',
};
const COST_KEY: Readonly<Record<string, string>> = {
  none: 'pfCostNone', product_live: 'pfCostLive', product_oplog: 'pfCostOplog', reorg_shortfall: 'pfCostReorg', settings_rate: 'pfCostSettings',
};
// 来源 → 用户 360 页签（无单据级抽屉入口的来源落到所属用户的对应页签；代理提佣无用户，仅抽屉列示）
const SOURCE_TAB: Readonly<Record<string, U360Tab>> = {
  card_action: 'cards', card_transaction: 'spend', deposit: 'funds', withdrawal: 'funds', crypto_reorg_hold: 'funds', wallet_ledger: 'funds',
};

// 未知值回落原值（X-34）
const mapped = (t: Dict, map: Readonly<Record<string, string>>, v: string): string => {
  const k = map[v];
  return k ? str(t, k) : v;
};
export const typeLabel = (t: Dict, v: string): string => mapped(t, TYPE_KEY, v);

// ---- 展示名缓存（facetLabel 无宿主参数） ----

let labelCache: ProfitLabels = { agents: {}, products: {} };

function isStrMap(x: unknown): x is Readonly<Record<string, string>> {
  return typeof x === 'object' && x !== null && Object.values(x).every((v) => typeof v === 'string');
}

function rememberLabels(page: Readonly<Record<string, unknown>>): void {
  const l = page['labels'];
  if (typeof l !== 'object' || l === null) return;
  const agents: unknown = Reflect.get(l, 'agents');
  const products: unknown = Reflect.get(l, 'products');
  labelCache = { agents: isStrMap(agents) ? { ...labelCache.agents, ...agents } : labelCache.agents, products: isStrMap(products) ? { ...labelCache.products, ...products } : labelCache.products };
}

const agentLabel = (t: Dict, id: string): string => (id === '' ? str(t, 'pfDirect') : labelCache.agents[id] ?? `#${id}`);
const productLabel = (t: Dict, id: string): string => (id === '' ? str(t, 'pfNoProduct') : labelCache.products[id] ?? `#${id}`);
const simLabel = (t: Dict, v: string): string => str(t, v === '1' ? 'pfSimYes' : 'pfSimNo');

// ---- 汇总响应头 ----

const SUM_KEYS = ['gross', 'agent', 'platform', 'invite', 'cost', 'net', 'owed'] as const;

function isSums(x: unknown): x is ProfitSums {
  return typeof x === 'object' && x !== null && typeof Reflect.get(x, 'count') === 'number' && SUM_KEYS.every((k) => typeof Reflect.get(x, k) === 'string');
}

function isObj(x: unknown, keys: readonly string[]): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && keys.every((k) => typeof Reflect.get(x, k) === 'string');
}

export function summaryHead(page: Readonly<Record<string, unknown>>): Pick<ProfitSummaryHead, 'totals' | 'manual_adjust' | 'agent_payable' | 'build'> | null {
  const totals = page['totals'];
  const manual = page['manual_adjust'];
  const payable = page['agent_payable'];
  const build = page['build'];
  if (!isSums(totals) || !isObj(manual, ['credit', 'debit', 'net']) || !isObj(payable, ['collected', 'paid', 'outstanding']) || typeof build !== 'object' || build === null) return null;
  const count = Reflect.get(manual, 'count');
  const wm: unknown = Reflect.get(build, 'watermark');
  const lr: unknown = Reflect.get(build, 'last_recon');
  const recon = isObj(lr, ['date', 'status']) ? { date: String(lr['date']), status: String(lr['status']) } : null;
  return {
    totals,
    manual_adjust: { credit: String(manual['credit']), debit: String(manual['debit']), net: String(manual['net']), count: typeof count === 'number' ? count : 0 },
    agent_payable: { collected: String(payable['collected']), paid: String(payable['paid']), outstanding: String(payable['outstanding']) },
    build: { watermark: typeof wm === 'string' ? wm : null, last_recon: recon },
  };
}

// 06 C-32 统计卡：用户实付 / 代理所得 / 邀请奖励 / 已知成本 / 平台净利润（dark）/ 调账净额（未计入）/ 应付代理（未付；sub 代收 · 已付）
function summaryStats(ctx: AdminCtx, page: Readonly<Record<string, unknown>>): readonly StatVals[] {
  rememberLabels(page);
  const t = ctx.t;
  const h = summaryHead(page);
  const v = (x: string | undefined): string => (h && x !== undefined ? usdt(x) : '—');
  const build = h?.build;
  const buildSub = build?.watermark
    ? interpolate(str(t, 'pfSubBuild'), { t: time(ctx, build.watermark), d: build.last_recon?.date ?? str(t, 'pfReconNone'), s: build.last_recon?.status ?? '' }).trim()
    : str(t, 'pfSubBuildNone');
  return [
    { label: str(t, 'pfKGross'), value: v(h?.totals.gross), sub: h ? interpolate(str(t, 'pfSubCount'), { n: h.totals.count, owed: amt(h.totals.owed) }) : '' },
    { label: str(t, 'pfKAgent'), value: v(h?.totals.agent), sub: h ? interpolate(str(t, 'pfSubPlatform'), { v: signed(h.totals.platform) }) : '' },
    { label: str(t, 'pfKInvite'), value: v(h?.totals.invite), sub: str(t, 'pfSubInvite') },
    { label: str(t, 'pfKCost'), value: v(h?.totals.cost), sub: str(t, 'pfSubCost') },
    { label: str(t, 'pfKNet'), value: v(h?.totals.net), sub: buildSub, dark: true },
    { label: str(t, 'pfKManual'), value: v(h?.manual_adjust.net), sub: h ? interpolate(str(t, 'pfSubManual'), { credit: amt(h.manual_adjust.credit), debit: amt(h.manual_adjust.debit), n: h.manual_adjust.count }) : '' },
    { label: str(t, 'pfKPayable'), value: v(h?.agent_payable.outstanding), sub: h ? interpolate(str(t, 'pfSubPayable'), { collected: amt(h.agent_payable.collected), paid: amt(h.agent_payable.paid) }) : '' },
  ];
}

// ---- 汇总视图 ----

const money = (v: string, dim = false): CellVals => {
  const c = cell(signed(v));
  return dim ? { ...c, style: `${c.style};${GRAY}` } : negative(v) ? { ...c, style: `${c.style};${RED}` } : c;
};

const AMOUNT_COLS: readonly ColDef[] = [
  { label: 'pfCCount' }, { label: 'pfCGross', unit: 'USDT' }, { label: 'pfCAgentAmt', unit: 'USDT' }, { label: 'pfCInvite', unit: 'USDT' },
  { label: 'pfCCost', unit: 'USDT' }, { label: 'pfCNet', unit: 'USDT' },
];

function amountCells(r: ProfitGroupRow): CellVals[] {
  const dim = !r.in_net;
  const count = cell(r.count);
  return [dim ? { ...count, style: `${count.style};${GRAY}` } : count, money(r.gross, dim), money(r.agent, dim), money(r.invite, dim), money(r.cost, dim), money(r.net, dim)];
}

const SUMMARY_NOTE = (t: Dict): string => str(t, 'pfNote');

function groupView(key: 'type' | 'agent' | 'day' | 'product', first: ColDef, head: (ctx: AdminCtx, r: ProfitGroupRow) => CellVals, extra?: { col: ColDef; cell: (r: ProfitGroupRow) => CellVals }) {
  return defineView<ProfitGroupRow>({
    key,
    source: { route: 'GET /api/admin/profit/summary', tabParam: 'tab' },
    echo: '/admin/profit/summary',
    cols: extra ? [first, ...AMOUNT_COLS.slice(0, 3), extra.col, ...AMOUNT_COLS.slice(3)] : [first, ...AMOUNT_COLS],
    cells: (c, r) => {
      const base = amountCells(r);
      const cells = [head(c, r), ...base];
      return extra ? [...cells.slice(0, 4), extra.cell(r), ...cells.slice(4)] : cells;
    },
    stats: summaryStats,
    note: SUMMARY_NOTE,
    csvName: `profit-${key}`,
    noKeyword: true,
    noExport: true, // 汇总不出 CSV（format → 4220），导出走明细页签
  });
}

const typeView = groupView('type', { label: 'pfCGroupType', facet: 'entry_type', facetLabel: typeLabel }, (c, r) => {
  const x = cell(typeLabel(c.t, r.key), r.in_net ? '' : str(c.t, 'pfNotIn'));
  return r.in_net ? x : { ...x, style: `${x.style};${GRAY}` };
});
const agentView = groupView(
  'agent',
  { label: 'pfCAgent', facet: 'agent_id', facetLabel: agentLabel },
  (c, r) => cell(r.key === '' ? str(c.t, 'pfDirect') : r.label || `#${r.key}`, r.key === '' ? '' : `#${r.key}`),
  { col: { label: 'pfCSelf', unit: 'USDT' }, cell: (r) => money(r.agent_self_earned ?? '0') },
);
const dayView = groupView('day', { label: 'pfCDay' }, (_c, r) => cell(r.key, '', true));
const productView = groupView('product', { label: 'pfCProduct' }, (c, r) => cell(r.key === '' ? str(c.t, 'pfNoProduct') : r.label || `#${r.key}`, r.key === '' ? '' : `#${r.key}`));

// ---- 明细视图 ----

function openSource(ctx: AdminCtx, r: ProfitEntryRow): void {
  const tab = SOURCE_TAB[r.source_type];
  if (tab && r.user_id !== null) open360(ctx, r.user_id, tab);
}

const canSource = (ctx: AdminCtx, r: ProfitEntryRow): boolean => r.user_id !== null && SOURCE_TAB[r.source_type] !== undefined && can(ctx.s.sh.me, 'users', 'view');

function entryDrawer(ctx: AdminCtx, r: ProfitEntryRow): DrawerSpec {
  const t = ctx.t;
  const yes = (b: boolean): string => str(t, b ? 'pfYes' : 'pfNo');
  const actions: DrawerAction[] = [];
  if (canSource(ctx, r)) actions.push({ label: str(t, 'pfOpenSource'), kind: '', run: (c) => openSource(c, r) });
  actions.push({ label: str(t, 'aClose'), kind: 'primary', run: (c) => closeDrawer(c) });
  return {
    kind: str(t, 'pfDrawerKind'),
    title: `${typeLabel(t, r.entry_type)} · ${usdt(r.gross)}`,
    subtitle: `${mapped(t, SOURCE_KEY, r.source_type)} ${r.source_biz_no ?? `#${r.source_id}`}`,
    fields: [
      { k: str(t, 'pfFSourceType'), v: mapped(t, SOURCE_KEY, r.source_type) },
      { k: str(t, 'pfFSourceNo'), v: r.source_biz_no ?? '—' },
      { k: str(t, 'pfFSourceId'), v: String(r.source_id) },
      { k: str(t, 'pfFOccurred'), v: time(ctx, r.occurred_at) },
      { k: str(t, 'pfCUser'), v: r.user_uid ?? '—' },
      { k: str(t, 'pfCAgent'), v: r.channel_agent_id === null ? str(t, 'pfDirect') : r.agent_biz_no ?? `#${r.channel_agent_id}` },
      { k: str(t, 'pfCProduct'), v: r.product_name ?? (r.product_id === null ? '—' : `#${r.product_id}`) },
      { k: str(t, 'pfCGross'), v: usdt(r.gross) },
      { k: str(t, 'pfCAgentAmt'), v: usdt(r.agent_amount) },
      { k: str(t, 'pfFPlatform'), v: usdt(r.platform_amount) },
      { k: str(t, 'pfCInvite'), v: usdt(r.invite_amount) },
      { k: str(t, 'pfCCost'), v: usdt(r.cost_amount) },
      { k: str(t, 'pfCNet'), v: usdt(r.net_amount) },
      { k: str(t, 'pfCOwed'), v: usdt(r.owed_amount) },
      { k: str(t, 'pfFCostBasis'), v: mapped(t, COST_KEY, r.cost_basis) },
      { k: str(t, 'pfFInNet'), v: yes(r.in_net) },
      { k: str(t, 'pfCSim'), v: yes(r.is_simulated) },
      { k: str(t, 'pfFBuilt'), v: time(ctx, r.built_at) },
      { k: str(t, 'pfFRebuilt'), v: r.rebuilt_at === null ? str(t, 'pfNever') : interpolate(str(t, 'pfRebuiltSub'), { t: time(ctx, r.rebuilt_at), n: r.rebuild_count }) },
    ],
    actions,
  };
}

const openEntry = (c: AdminCtx, r: ProfitEntryRow): void => openDrawer(c, entryDrawer(c, r));

const entriesView = defineView<ProfitEntryRow>({
  key: 'entries',
  source: { route: 'GET /api/admin/profit/entries', tabParam: 'none' },
  echo: '/admin/profit/entries',
  cols: [
    { label: 'cTime', sortKey: 'occurred_at' }, { label: 'cType', facet: 'entry_type', facetLabel: typeLabel }, { label: 'pfCSource' }, { label: 'pfCUser' },
    { label: 'pfCAgent', facet: 'agent_id', facetLabel: agentLabel }, { label: 'pfCProduct', facet: 'product_id', facetLabel: productLabel },
    { label: 'pfCGross', unit: 'USDT' }, { label: 'pfCAgentAmt', unit: 'USDT' }, { label: 'pfCCost', unit: 'USDT' }, { label: 'pfCNet', unit: 'USDT' },
    { label: 'pfCSim', facet: 'include_simulated', facetLabel: simLabel },
  ],
  cells: (c, r) => {
    const t = c.t;
    const dim = !r.in_net;
    const ty = cell(typeLabel(t, r.entry_type), dim ? str(t, 'pfNotIn') : '');
    return [
      cell(time(c, r.occurred_at)),
      dim ? { ...ty, style: `${ty.style};${GRAY}` } : ty,
      cell(r.source_biz_no ?? `#${r.source_id}`, mapped(t, SOURCE_KEY, r.source_type), true),
      cell(r.user_uid ?? '—', '', true),
      cell(r.channel_agent_id === null ? str(t, 'pfDirect') : r.agent_biz_no ?? `#${r.channel_agent_id}`),
      cell(r.product_name ?? '—'),
      money(r.gross, dim),
      { ...money(r.agent_amount, dim), sub: r.invite_amount !== '0.000000' ? `${str(t, 'pfCInvite')} ${signed(r.invite_amount)}` : '' },
      money(r.cost_amount, dim),
      money(r.net_amount, dim),
      cell(str(t, r.is_simulated ? 'pfYes' : 'pfNo')),
    ];
  },
  open: openEntry,
  // 用户 360 需 users.view：无位不渲染（与抽屉「查看来源」同口径）
  rowActions: (ctx): readonly ActionDef<ProfitEntryRow>[] => [
    { label: 'aDetail', run: openEntry },
    { label: 'pfOpenUser', show: (r) => r.user_id !== null && can(ctx.s.sh.me, 'users', 'view'), run: (c, r) => (r.user_id === null ? undefined : open360(c, r.user_id)) },
  ],
  stats: (_c, page) => {
    rememberLabels(page);
    return [];
  },
  note: SUMMARY_NOTE,
  csvName: 'profit-entries',
});

// TD-849：页文案包
const PACK = { i18n: I18N_PROFIT, rewrites: REWRITES_PROFIT } as const;

export const PAGES_PROFIT: PageMap = {
  profit: {
    page: 'profit',
    ...PACK,
    tabLabels: (t) => [str(t, 'pfTabType'), str(t, 'pfTabAgent'), str(t, 'pfTabDay'), str(t, 'pfTabProduct'), str(t, 'pfTabEntries')],
    views: [typeView, agentView, dayView, productView, entriesView],
  },
};
